// Dashboard widget catalogue and grid reconciliation.
// Every station gets a now-playing hero, on by default and always listed
// in Customize so it can be turned back on.

export const GRID_BREAKPOINTS = { lg: 900, xs: 0 };
export const GRID_COLS = { lg: 12, xs: 2 };
export const GRID_ROW_HEIGHT = 80;
export const GRID_MARGIN = [20, 20];

const MULTI_REGION_HERO_SIZE = { w: 12, h: 6, minW: 6, minH: 4 };

const WIDGET_SIZE_DEFAULTS = {
  station: { w: 12, h: 4, minW: 6, minH: 3 },
  clock: { w: 4, h: 2, minW: 2, minH: 2 },
  'on-air': { w: 4, h: 3, minW: 3, minH: 2 },
  'playlist-coverage': { w: 4, h: 3, minW: 3, minH: 2 },
};

export const WIDGET_GROUPS = [
  {
    id: 'now-playing',
    title: 'Station heroes',
    hint: 'Each station has a now-playing hero. It starts on, and stays available here if you hide it.',
  },
  {
    id: 'overview',
    title: 'Other widgets',
    hint: 'Summaries you can add alongside the heroes.',
  },
];

// Not `station-<id>`: that id belonged to the old opt-in station card, and a
// saved removal of that card must not hide the hero.
export const stationWidgetId = (stationId) => `station-hero-${stationId}`;

export const sizeForWidget = (widget) => (
  widget?.size || WIDGET_SIZE_DEFAULTS[widget?.type] || { w: 6, h: 4, minW: 2, minH: 2 }
);

export const sortGridItems = (items) => [...(items || [])].sort((a, b) => (a.y - b.y) || (a.x - b.x));

export function buildDashboardWidgets(stations = []) {
  const stationWidgets = (Array.isArray(stations) ? stations : []).map((station) => ({
    id: stationWidgetId(station.id),
    label: station.name || `Station ${station.id}`,
    description: 'Hero with artwork, progress, and up next',
    type: 'station',
    group: 'now-playing',
    station,
    defaultOn: true,
    ...(station.mode === 'multi-region' ? { size: MULTI_REGION_HERO_SIZE } : {}),
  }));

  return [
    ...stationWidgets,
    {
      id: 'clock',
      label: 'Clock',
      description: 'Large clock and today’s date',
      type: 'clock',
      group: 'overview',
      defaultOn: false,
    },
    {
      id: 'on-air',
      label: 'On-air summary',
      description: 'Every station on one list',
      type: 'on-air',
      group: 'overview',
      defaultOn: false,
    },
    {
      id: 'playlist-coverage',
      label: 'Playlist coverage',
      description: 'How far ahead each station is scheduled',
      type: 'playlist-coverage',
      group: 'overview',
      defaultOn: false,
    },
  ];
}

export function deriveXsLayout(lgItems, widgetsById) {
  let y = 0;
  return sortGridItems(lgItems).map((item) => {
    const widget = widgetsById[item.i];
    const size = widget ? sizeForWidget(widget) : { h: 4, minH: 2 };
    const h = Math.max(item.h || size.h, size.minH || 1);
    const placed = { i: item.i, x: 0, y, w: GRID_COLS.xs, h, minW: 1, minH: size.minH || 1 };
    y += h;
    return placed;
  });
}

export function buildLgLayout(widgets) {
  const items = [];
  let cursorX = 0;
  let cursorY = 0;
  let rowMaxH = 0;

  widgets.forEach((widget) => {
    const size = sizeForWidget(widget);
    if (cursorX + size.w > GRID_COLS.lg) {
      cursorX = 0;
      cursorY += rowMaxH;
      rowMaxH = 0;
    }
    items.push({
      i: widget.id,
      x: cursorX,
      y: cursorY,
      w: size.w,
      h: size.h,
      minW: size.minW,
      minH: size.minH,
    });
    cursorX += size.w;
    rowMaxH = Math.max(rowMaxH, size.h);
  });

  return items;
}

export function buildDefaultLayouts(widgets, removed = {}) {
  const activeWidgets = widgets.filter((widget) => widget.defaultOn && !removed[widget.id]);
  const lg = buildLgLayout(activeWidgets);
  const widgetsById = Object.fromEntries(widgets.map((widget) => [widget.id, widget]));
  return { lg, xs: deriveXsLayout(lg, widgetsById) };
}

export function layoutsAreEqual(a, b) {
  const pick = (items) => sortGridItems(items).map(({ i, x, y, w, h }) => `${i}:${x},${y},${w},${h}`).join('|');
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const key of keys) {
    if (pick(a?.[key]) !== pick(b?.[key])) return false;
  }
  return true;
}

function widgetsByIdFrom(widgets) {
  return Object.fromEntries(widgets.map((widget) => [widget.id, widget]));
}

function normalizeRemoved(removedWidgets, validIds) {
  const next = {};
  if (!removedWidgets || typeof removedWidgets !== 'object') return next;
  Object.keys(removedWidgets).forEach((id) => {
    if (validIds.has(id) && removedWidgets[id] === true) next[id] = true;
  });
  return next;
}

function sameRemoved(prev, next) {
  const prevIds = Object.keys(prev || {}).filter((id) => prev[id] === true);
  const nextIds = Object.keys(next || {});
  if (prevIds.length !== nextIds.length) return false;
  return nextIds.every((id) => prev[id] === true);
}

function appendGridItems(items, entries) {
  let y = items.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  const next = [...items];
  entries.forEach((entry) => {
    next.push({
      i: entry.i,
      x: 0,
      y,
      w: entry.w,
      h: entry.h,
      minW: entry.minW,
      minH: entry.minH,
    });
    y += entry.h;
  });
  return next;
}

function lgEntry(widget) {
  const size = sizeForWidget(widget);
  return { i: widget.id, w: size.w, h: size.h, minW: size.minW, minH: size.minH };
}

function xsEntry(item, widgetsById) {
  const widget = widgetsById[item.i];
  const size = widget ? sizeForWidget(widget) : { h: 4, minH: 2 };
  return {
    i: item.i,
    w: GRID_COLS.xs,
    h: Math.max(item.h || size.h, size.minH || 1),
    minW: 1,
    minH: size.minH || 1,
  };
}

function placeMissing(lg, missing, { prepend }) {
  if (missing.length === 0) return lg;
  if (prepend) {
    const prepended = buildLgLayout(missing);
    const shift = prepended.reduce((max, item) => Math.max(max, item.y + item.h), 0);
    return [
      ...prepended,
      ...lg.map((item) => ({ ...item, y: item.y + shift })),
    ];
  }
  return appendGridItems(lg, missing.map(lgEntry));
}

/**
 * Drop unknown widgets (including the old single "Top Now Playing" hero and
 * old station cards), keep explicitly hidden widgets hidden, and make sure
 * every station hero is on the dashboard unless the user turned it off.
 */
export function reconcileDashboardLayout({
  layouts,
  removedWidgets,
  widgets,
  legacyOrder = null,
}) {
  const widgetsById = widgetsByIdFrom(widgets);
  const validIds = new Set(widgets.map((widget) => widget.id));
  const removed = normalizeRemoved(removedWidgets, validIds);
  const defaults = widgets.filter((widget) => widget.defaultOn && !removed[widget.id]);

  let lg = (layouts?.lg || []).filter((item) => validIds.has(item.i) && !removed[item.i]);
  let consumeLegacyOrder = false;
  let prependedDefaults = false;

  if (lg.length === 0) {
    consumeLegacyOrder = true;
    let ordered = defaults;
    if (Array.isArray(legacyOrder) && legacyOrder.length > 0) {
      const fromOrder = legacyOrder
        .map((id) => widgetsById[id])
        .filter((widget) => widget && !removed[widget.id]);
      const fromOrderIds = new Set(fromOrder.map((widget) => widget.id));
      const missingDefaults = defaults.filter((widget) => !fromOrderIds.has(widget.id));
      ordered = [...missingDefaults, ...fromOrder];
    }
    lg = buildLgLayout(ordered);
  } else {
    const present = new Set(lg.map((item) => item.i));
    const missing = defaults.filter((widget) => !present.has(widget.id));
    const hasStationHero = lg.some((item) => widgetsById[item.i]?.type === 'station');
    prependedDefaults = missing.length > 0 && !hasStationHero;
    lg = placeMissing(lg, missing, { prepend: prependedDefaults });
  }

  let xs = (layouts?.xs || []).filter((item) => validIds.has(item.i) && !removed[item.i]);
  const lgIds = new Set(lg.map((item) => item.i));
  xs = xs.filter((item) => lgIds.has(item.i));
  if (xs.length === 0 || prependedDefaults) {
    xs = deriveXsLayout(lg, widgetsById);
  } else {
    const xsIds = new Set(xs.map((item) => item.i));
    const missing = sortGridItems(lg).filter((item) => !xsIds.has(item.i));
    xs = appendGridItems(xs, missing.map((item) => xsEntry(item, widgetsById)));
  }

  const nextLayouts = { lg, xs };
  return {
    layouts: nextLayouts,
    removedWidgets: removed,
    consumeLegacyOrder,
    layoutsChanged: !layoutsAreEqual(layouts, nextLayouts),
    removedChanged: !sameRemoved(removedWidgets, removed),
  };
}
